import React from "react"
import {useTranslation} from "react-i18next"
import {XMarkIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import {AKlaraDashboardDealsMeta, AKlaraDashboardDealViewValues} from "../../../services/types.generated.ts"
import {KlaraFiltersContext, TKlaraView} from "../filters.tsx"
import {statusLabel} from "../status.ts"

type TViewCounts = AKlaraDashboardDealsMeta["counts"]["views"]

const VIEWS: TKlaraView[] = [
  AKlaraDashboardDealViewValues.Attention,
  AKlaraDashboardDealViewValues.All,
  AKlaraDashboardDealViewValues.High,
  AKlaraDashboardDealViewValues.Rotten,
  AKlaraDashboardDealViewValues.Closed,
]

/**
 * The view tabs with their counts; a status chip replaces the selected tab until it is cleared.
 * Counts of a previous filter or a refreshing response stay dimmed until the current response settles.
 */
export const LeadsTabs: React.FC<{counts: TViewCounts | undefined; isStale: boolean}> = ({counts, isStale}) => {
  const {t} = useTranslation()
  const {view, setView, chip, setChip, priorities, search, salesPersonExternalId, clearFilters} =
    KlaraFiltersContext.useContext()
  const hasFilters = chip !== null || priorities.length > 0 || search !== "" || salesPersonExternalId !== undefined

  return (
    <div className={"mt-3.5 flex flex-wrap items-center gap-1 border-b border-cr-slate-15"}>
      <div role={"group"} aria-label={t("KlaraDashboard_Leads_Views")} className={"flex flex-wrap gap-1"}>
        {VIEWS.map(item => {
          const isActive = chip === null && view === item

          return (
            <button
              key={item}
              type={"button"}
              aria-pressed={isActive}
              onClick={() => setView(item)}
              className={twMerge(
                "-mb-px inline-flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-[13px] font-semibold whitespace-nowrap text-cr-slate-60 transition-colors hover:text-cr-blue-dark",
                isActive && "border-cr-blue text-cr-blue hover:text-cr-blue"
              )}
            >
              {viewLabel(t, item)}
              {counts && (
                <span
                  aria-busy={isStale}
                  className={twMerge(
                    "rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums transition-opacity",
                    isActive ? "bg-cr-blue-light text-cr-blue-dark" : "bg-cr-slate-6 text-cr-slate-60",
                    isStale && "opacity-50"
                  )}
                >
                  {counts[item]}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {chip && (
        <button
          type={"button"}
          onClick={() => setChip(null)}
          aria-label={t("KlaraDashboard_Leads_ClearChip", {name: statusLabel(chip)})}
          className={
            "ml-2 inline-flex items-center gap-1 rounded-full bg-cr-blue-light py-1 pr-2 pl-2.5 text-[11.5px] font-semibold text-cr-blue-dark hover:bg-cr-blue-mid-light"
          }
        >
          {statusLabel(chip)}
          <XMarkIcon className={"size-4 shrink-0"} aria-hidden={"true"} />
        </button>
      )}
      {hasFilters && (
        <button
          type={"button"}
          onClick={clearFilters}
          className={"ml-auto rounded-[7px] px-2 py-1.5 text-xs font-semibold text-cr-blue hover:underline"}
        >
          {t("KlaraDashboard_Leads_ClearFilters")}
        </button>
      )}
    </div>
  )
}

const viewLabel = (t: ReturnType<typeof useTranslation>["t"], view: TKlaraView) => {
  switch (view) {
    case AKlaraDashboardDealViewValues.Attention:
      return t("KlaraDashboard_Leads_View_Attention")
    case AKlaraDashboardDealViewValues.All:
      return t("KlaraDashboard_Leads_View_All")
    case AKlaraDashboardDealViewValues.High:
      return t("KlaraDashboard_Leads_View_High")
    case AKlaraDashboardDealViewValues.Rotten:
      return t("KlaraDashboard_Leads_View_Rotten")
    case AKlaraDashboardDealViewValues.Closed:
      return t("KlaraDashboard_Leads_View_Closed")
  }
}
